<template>
  <div class="night-consent-modal">
    <c-alert :title="titleText" is-close-btn @close="onClose">
      <template #content>
        <div class="modal-content">
          <div class="night-consent">
            <p class="night-consent__desc">{{ descText }}</p>

            <div class="night-consent__choices">
              <button
                v-for="choice in CHOICES"
                :key="choice.value"
                type="button"
                class="night-consent__choice"
                :class="{ 'is-selected': selectedConsent === choice.value }"
                @click="selectConsent(choice.value)"
              >
                <span class="night-consent__choice-icon" v-html="choice.icon" />
                <span class="night-consent__choice-label">{{ choice.label }}</span>
              </button>
            </div>

            <div v-if="isAgree" class="night-consent__chips">
              <button
                v-for="minute in MINUTES"
                :key="minute.value"
                type="button"
                class="night-consent__chip"
                :class="{ 'is-selected': selectedMinute === minute.value }"
                @click="selectMinute(minute.value)"
              >
                {{ minute.label }}
              </button>
            </div>

            <div class="night-consent__footer">
              <c-button
                class="night-consent__cta"
                :size="CButtonSize.L"
                :type="CButtonType.Fill"
                :variant="CButtonVariant.Primary"
                :label="ctaText"
                :disabled="selectedConsent === null"
                @click="onConfirm"
              />
            </div>
          </div>
        </div>
      </template>
    </c-alert>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { CAlert, CButton, CButtonSize, CButtonType, CButtonVariant } from '@aj-fe/aj-vue-core'

type ConsentValue = 'agree' | 'refuse'
type CallbackMinute = 30 | 60 | 120

// CIcon(175 dynamic chunk) 미사용 원칙 — 해/달 아이콘은 인라인 SVG.
const SUN_ICON =
  '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="14" cy="14" r="5.5" fill="#FFB020"/><g stroke="#FFB020" stroke-width="2" stroke-linecap="round"><line x1="14" y1="2.5" x2="14" y2="5"/><line x1="14" y1="23" x2="14" y2="25.5"/><line x1="2.5" y1="14" x2="5" y2="14"/><line x1="23" y1="14" x2="25.5" y2="14"/><line x1="5.8" y1="5.8" x2="7.6" y2="7.6"/><line x1="20.4" y1="20.4" x2="22.2" y2="22.2"/><line x1="5.8" y1="22.2" x2="7.6" y2="20.4"/><line x1="20.4" y1="7.6" x2="22.2" y2="5.8"/></g></svg>'
const MOON_ICON =
  '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.5 17.8A9 9 0 0 1 10.2 5.5 7.5 7.5 0 1 0 22.5 17.8Z" fill="#FDB022"/></svg>'

// [공용 컴포넌트 슬롯 정의] 개방 3 · 고정 2
// - 개방(override 가능): 메인텍스트(title) · 서브텍스트(desc) · CTA(ctaLabel)
// - 고정(override 불가, 콜백 로직에 물림): 선택지 라벨(CHOICES) · 시간옵션(MINUTES)
const DEFAULT_TITLE = '언제 연락드릴까요?'
const DEFAULT_DESC =
  '야간(오후 9시~오전 8시)에는 고객님 동의 없이 전화드리지 않아요. 지금 연락드릴까요?'
const DEFAULT_CTA = '비밀혜택 안내 받기'

const props = withDefaults(
  defineProps<{ title?: string; desc?: string; ctaLabel?: string }>(),
  { title: '', desc: '', ctaLabel: '' },
)

const titleText = computed(() => props.title || DEFAULT_TITLE)
const descText = computed(() => props.desc || DEFAULT_DESC)
const ctaText = computed(() => props.ctaLabel || DEFAULT_CTA)

const emit = defineEmits<{
  (
    e: 'confirm',
    payload: { isNightCallReceiveConsent: boolean; nightCallbackRequestMinute: number | null },
  ): void
  (e: 'close'): void
}>()

const CHOICES: { value: ConsentValue; icon: string; label: string }[] = [
  { value: 'refuse', icon: SUN_ICON, label: '낮에 받을게요' },
  { value: 'agree', icon: MOON_ICON, label: '지금 가능해요' },
]
const MINUTES: { value: CallbackMinute; label: string }[] = [
  { value: 30, label: '30분 내' },
  { value: 60, label: '1시간 내' },
  { value: 120, label: '2시간 내' },
]

const selectedConsent = ref<ConsentValue | null>(null)
const selectedMinute = ref<CallbackMinute>(30)
const isAgree = computed(() => selectedConsent.value === 'agree')

const selectConsent = (value: ConsentValue) => {
  selectedConsent.value = value
}
const selectMinute = (value: CallbackMinute) => {
  selectedMinute.value = value
}

const onConfirm = () => {
  if (!selectedConsent.value) return
  if (selectedConsent.value === 'agree') {
    emit('confirm', { isNightCallReceiveConsent: true, nightCallbackRequestMinute: selectedMinute.value })
  } else {
    emit('confirm', { isNightCallReceiveConsent: false, nightCallbackRequestMinute: null })
  }
}

const onClose = () => emit('close')
</script>

<style lang="scss">
@use '@aj-fe/aj-vue-core/styles/theme' as theme;
@use './modal-shared' as shared;

.night-consent-modal {
  @include shared.alert-card;

  .c-alert-container .modal-content {
    .night-consent {
      display: flex;
      flex-direction: column;
      gap: 20px;
      text-align: left;

      &__desc {
        @include theme.Body-M-500;
        color: var(--c-gray700, #575e64);
        word-break: keep-all;

        @media (max-width: 1024px) {
          @include theme.Body-S-500;
        }
      }

      &__choices {
        display: flex;
        gap: 8px;
      }

      &__choice {
        flex: 1 0 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 16px;
        border: 1px solid var(--c-gray300, #dee2e6);
        border-radius: 12px;
        background: var(--c-white, #fff);
        cursor: pointer;
        transition:
          border-color 0.15s ease,
          background 0.15s ease;

        &.is-selected {
          border-color: var(--c-primary600, #145ce6);
          background: var(--c-primary50, #e8effd);

          .night-consent__choice-label {
            color: var(--c-primary600, #145ce6);
          }
        }
      }

      &__choice-icon {
        display: inline-flex;
        width: 28px;
        height: 28px;
      }

      &__choice-label {
        @include theme.Body-S-500;
        color: var(--c-gray900, #1f2429);
      }

      &__chips {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }

      &__chip {
        padding: 8px 16px;
        border: 1px solid var(--c-gray300, #dee2e6);
        border-radius: 999px;
        background: var(--c-white, #fff);
        color: var(--c-gray700, #575e64);
        @include theme.Body-S-500;
        cursor: pointer;
        transition:
          border-color 0.15s ease,
          background 0.15s ease,
          color 0.15s ease;

        &.is-selected {
          border-color: var(--c-primary600, #145ce6);
          background: var(--c-primary50, #e8effd);
          color: var(--c-primary600, #145ce6);
        }
      }

      &__footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 4px;
      }
    }
  }
}
</style>
